import React, { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid2";
import Chip from "@mui/material/Chip";
import CommonModal from "./CommonModal";
import {
  getCustomerBalance,
  checkContractThreshold,
  CustomerBalanceData,
  ContractThresholdData,
} from "../../utils/balanceService";
import {
  Wallet,
  FileText,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  User,
} from "lucide-react";
import BillingIcon from "../../assets/svg/billing";

interface BalanceDetailModalProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
}

const BalanceDetailModal: React.FC<BalanceDetailModalProps> = ({
  open,
  onClose,
  customerId,
}) => {
  const [loading, setLoading] = useState(false);
  const [balanceData, setBalanceData] = useState<CustomerBalanceData | null>(
    null,
  );
  const [thresholds, setThresholds] = useState<Record<string, boolean>>({});

  const fetchData = useCallback(async () => {
    if (!customerId || !open) return;

    setLoading(true);
    try {
      const data = await getCustomerBalance(customerId);
      setBalanceData(data);

      if (data?.contractBalances) {
        const thresholdResults: Record<string, boolean> = {};
        await Promise.all(
          data.contractBalances.map(async (contract) => {
            const threshold = await checkContractThreshold(contract.contractId);
            if (threshold) {
              thresholdResults[contract.contractId] =
                threshold.thresholdReached;
            }
          }),
        );
        setThresholds(thresholdResults);
      }
    } catch (error) {
      console.error("Error in BalanceDetailModal fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [customerId, open]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const body = (
    <Box sx={{ minHeight: "300px", py: 0 }}>
      {loading ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height: "300px" }}
        >
          <CircularProgress size={40} sx={{ color: "#1064ED" }} />
          <Typography
            sx={{ mt: 2, fontWeight: 500, color: "#64748B" }}
            variant="body1"
          >
            Fetching balance details...
          </Typography>
        </Stack>
      ) : balanceData ? (
        <Box>
          {/* Customer Info Strip */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mb: 3,
              p: 2,
              borderRadius: "8px",
              backgroundColor: "#F8F9FB",
              border: "1px solid #464A4F1A",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  backgroundColor: "#fff",
                  p: 1,
                  borderRadius: "6px",
                  display: "flex",
                  border: "1px solid #464A4F1A",
                }}
              >
                <User size={20} color="#1064ED" />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="#64748B"
                  sx={{ fontWeight: 600 }}
                >
                  CUSTOMER CODE
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {balanceData.customerCode}
                </Typography>
              </Box>
            </Stack>

            {balanceData.contractBalances.length === 1 && (
              <Stack alignItems="center" spacing={0.5}>
                <Chip
                  label={
                    thresholds[balanceData.contractBalances[0].contractId]
                      ? "Threshold Reached"
                      : "Threshold Not Reached"
                  }
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "9px",
                    height: "20px",
                    backgroundColor: thresholds[
                      balanceData.contractBalances[0].contractId
                    ]
                      ? "#FFEDED"
                      : "#E8F5E9",
                    color: thresholds[
                      balanceData.contractBalances[0].contractId
                    ]
                      ? "#D32F2F"
                      : "#2E7D32",
                    border: "none",
                  }}
                />
                <Typography
                  variant="caption"
                  color="#64748B"
                  sx={{
                    fontWeight: 500,
                    fontSize: "8px",
                    opacity: 0.8,
                    whiteSpace: "nowrap",
                  }}
                >
                  Last Synced:{" "}
                  {new Date(
                    balanceData.contractBalances[0].lastUpdated,
                  ).toLocaleString()}
                </Typography>
              </Stack>
            )}

            <Box sx={{ textAlign: "right" }}>
              <Typography
                variant="caption"
                color="#64748B"
                sx={{ fontWeight: 600 }}
              >
                TOTAL BALANCE
              </Typography>
              <Typography
                variant="h5"
                sx={{ color: "#1064ED", fontWeight: 800 }}
              >
                ₹ {balanceData.totalBalance.toLocaleString()}
              </Typography>
            </Box>
          </Stack>

          {balanceData.contractBalances.length > 1 && (
            <>
              <Divider sx={{ mb: 3, borderColor: "#464A4F26" }} />

              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "#181B25" }}
                >
                  Contract Balances
                </Typography>
              </Stack>

              {/* Contracts List */}
              <Stack spacing={2}>
                {balanceData.contractBalances.map((contract) => (
                  <Box
                    key={contract.contractId}
                    sx={{
                      p: 1.25,
                      px: 2,
                      borderRadius: "8px",
                      border: "1px solid #464A4F26",
                      backgroundColor: "#fff",
                      "&:hover": {
                        borderColor: "#1064ED66",
                        backgroundColor: "#F8F9FB",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      {/* Left: Contract Number Section */}
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ flexShrink: 0 }}
                      >
                        <Box
                          sx={{
                            p: 0.75,
                            borderRadius: "4px",
                            backgroundColor: "rgba(16, 100, 237, 0.08)",
                            color: "#1064ED",
                            display: "flex",
                          }}
                        >
                          <FileText size={14} />
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            color="#64748B"
                            sx={{ fontWeight: 600, fontSize: "9px" }}
                          >
                            CONTRACT NO.
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#1E293B",
                              lineHeight: 1.2,
                            }}
                          >
                            {contract.contractNumber}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Center: Status Section */}
                      <Box
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Chip
                          label={
                            thresholds[contract.contractId]
                              ? "Threshold Reached"
                              : "Threshold Not Reached"
                          }
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "9px",
                            height: "20px",
                            backgroundColor: thresholds[contract.contractId]
                              ? "#FFEDED"
                              : "#E8F5E9",
                            color: thresholds[contract.contractId]
                              ? "#D32F2F"
                              : "#2E7D32",
                            border: "none",
                          }}
                        />
                      </Box>

                      {/* Right: Balance & Sync Section */}
                      <Stack
                        alignItems="flex-end"
                        spacing={0}
                        sx={{ flexShrink: 0 }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="baseline"
                        >
                          <Typography
                            variant="caption"
                            color="#64748B"
                            sx={{ fontWeight: 600, fontSize: "9px" }}
                          >
                            BALANCE:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#1064ED", fontWeight: 700 }}
                          >
                            ₹{contract.currentBalance.toLocaleString()}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="caption"
                          color="#64748B"
                          sx={{
                            fontWeight: 500,
                            fontSize: "8px",
                            opacity: 0.8,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Last Synced:{" "}
                          {new Date(contract.lastUpdated).toLocaleString()}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Box>
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height: "300px" }}
        >
          <Box sx={{ mb: 2, color: "#D32F2F", opacity: 0.5 }}>
            <AlertCircle size={48} />
          </Box>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, color: "#475569" }}
          >
            Details Not Available
          </Typography>
          <Typography variant="body2" color="textSecondary">
            The balance information could not be loaded at this time.
          </Typography>
        </Stack>
      )}
    </Box>
  );

  return (
    <CommonModal
      title="Customer Balance Details"
      open={open}
      onClose={onClose}
      body={body}
      maxWidth="md"
      fullWidth
    />
  );
};

export default BalanceDetailModal;
